import React, { Fragment, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap-button-loader';
import notie from 'notie';
import '../../assets/css/notie.css';

const CLIENTES_SITIO_ENDPOINT = '/clientes_sitio'; // Endpoints respaldados por USUARIOS_SITIO_T
const MIN_PASSWORD_LENGTH = 5;

const initialClienteData = {
  id: null,
  nombre: '',
  apellido: '',
  correo: '',
  telefono: '',
  estado: 'Activo',
  activo: 1
};

const ActualizarCliente = (props) => {

  const idCliente = useMemo(() => props.pag_actualizar || props.match?.params?.id, [props.pag_actualizar, props.match]);
  const isEditing = !!idCliente;

  const [clienteData, setClienteData] = useState(initialClienteData);
  const [pass, setPass] = useState({ nueva: '', confirmar: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setClienteData(initialClienteData);
      setPass({ nueva: '', confirmar: '' });
      return;
    }

    const controller = new AbortController();
    const cargarCliente = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${CLIENTES_SITIO_ENDPOINT}/${idCliente}`, { signal: controller.signal });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.estado === 0) {
          throw new Error(payload?.resultado || 'No se pudo obtener el cliente');
        }
        const cliente = payload?.resultado || payload || {};
        setClienteData({
          id: cliente.id || idCliente,
          nombre: cliente.nombre || '',
          apellido: cliente.apellido || '',
          correo: cliente.correo || '',
          telefono: cliente.telefono || '',
          estado: cliente.estado || (cliente.activo === 0 ? 'Inactivo' : 'Activo'),
          activo: typeof cliente.activo === 'number' ? cliente.activo : (String(cliente.estado || '').toLowerCase() === 'inactivo' ? 0 : 1)
        });
        setPass({ nueva: '', confirmar: '' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error cargando cliente:', error);
        notie.alert({ type: 'error', text: error.message || 'No se pudo cargar la informacion del cliente', time: 3 });
      } finally {
        setLoading(false);
      }
    };

    cargarCliente();
    return () => controller.abort();
  }, [idCliente, isEditing]);

  const handleDataChange = (e) => {
    const { name, value } = e.target;
    setClienteData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'estado'
        ? { activo: String(value).toLowerCase() === 'inactivo' ? 0 : 1 }
        : {})
    }));
  };

  const handlePassChange = (name, value) => {
    setPass(prev => ({ ...prev, [name]: value }));
  };

  const validarPasswords = () => {
    const nueva = (pass.nueva || '').trim();
    const confirmar = (pass.confirmar || '').trim();
    const isPasswordChange = [nueva, confirmar].some(value => value.length > 0);

    if (!isPasswordChange) {
      return { isPasswordChange: false };
    }

    if (nueva.length < MIN_PASSWORD_LENGTH || confirmar.length < MIN_PASSWORD_LENGTH) {
      notie.alert({ type: 'warning', text: `La contrase\u00f1a debe tener minimo ${MIN_PASSWORD_LENGTH} caracteres.`, time: 2 });
      return null;
    }

    if (nueva !== confirmar) {
      notie.alert({ type: 'warning', text: 'La nueva contrase\u00f1a no coincide.', time: 2 });
      return null;
    }

    return { isPasswordChange: true, nueva };
  };

  const goBack = () => {
    if (typeof props.setPag_actualizar === 'function') {
      props.setPag_actualizar(0);
    } else if (props.history?.push) {
      props.history.push(`${process.env.PUBLIC_URL}/clientes`);
    }
  };

  const guardarCliente = () => {
    if (!isEditing) {
      notie.alert({ type: 'warning', text: 'No hay cliente seleccionado para actualizar.', time: 2 });
      return;
    }

    if (!clienteData.nombre || !clienteData.correo) {
      notie.alert({ type: 'warning', text: 'Nombre y correo son obligatorios.', time: 2 });
      return;
    }

    const passwordValidation = validarPasswords();
    if (!passwordValidation) return;

    const payload = {
      nombre: clienteData.nombre,
      apellido: clienteData.apellido,
      correo: clienteData.correo,
      telefono: clienteData.telefono,
      estado: clienteData.estado,
      activo: clienteData.activo,
      nueva_contrasena: passwordValidation.isPasswordChange ? (passwordValidation.nueva || '') : ''
    };

    const actualizar = async () => {
      setSaving(true);
      try {
        const response = await fetch(`${CLIENTES_SITIO_ENDPOINT}/${idCliente}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const resultado = await response.json().catch(() => ({}));
        if (!response.ok || resultado?.estado !== 1) {
          throw new Error(resultado?.resultado || 'No se pudo actualizar el cliente');
        }
        const actualizado = resultado?.resultado || {};
        notie.alert({ type: 'success', text: 'Cliente actualizado correctamente.', time: 2 });
        setClienteData(prev => ({
          ...prev,
          ...actualizado,
          estado: actualizado.estado || prev.estado,
          activo: typeof actualizado.activo === 'number' ? actualizado.activo : prev.activo
        }));
        setPass({ nueva: '', confirmar: '' });
        if (typeof props.onClienteActualizado === 'function') {
          props.onClienteActualizado();
        }
        goBack();
      } catch (error) {
        console.error('Error al actualizar cliente:', error);
        notie.alert({ type: 'error', text: error.message || 'No se pudo actualizar el cliente', time: 3 });
      } finally {
        setSaving(false);
      }
    };

    actualizar();
  };

  const reenviarContrasena = async () => {
    if (!isEditing) {
      notie.alert({ type: 'warning', text: 'Selecciona un cliente valido antes de reenviar la contrasena.', time: 2 });
      return;
    }
    if (!clienteData.correo) {
      notie.alert({ type: 'warning', text: 'El cliente no tiene correo registrado.', time: 2 });
      return;
    }

    setResending(true);
    try {
      const response = await fetch(`${CLIENTES_SITIO_ENDPOINT}/${idCliente}/reenviar_contrasena`, { method: 'POST' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.estado !== 1) {
        throw new Error(payload?.resultado || 'No se pudo reenviar la contrasena.');
      }
      notie.alert({ type: 'success', text: 'Correo enviado correctamente.', time: 2 });
    } catch (error) {
      console.error('Error reenviando contrasena:', error);
      notie.alert({ type: 'error', text: error.message || 'No se pudo reenviar la contrasena.', time: 3 });
    } finally {
      setResending(false);
    }
  };

  const guardarNuevaContrasena = async () => {
    if (!isEditing) {
      notie.alert({ type: 'warning', text: 'No hay cliente seleccionado.', time: 2 });
      return;
    }
    const passwordValidation = validarPasswords();
    if (!passwordValidation) return;
    if (!passwordValidation.isPasswordChange) {
      notie.alert({ type: 'warning', text: 'Ingresa la nueva contrasena en ambos campos.', time: 2 });
      return;
    }

    const payload = {
      nombre: clienteData.nombre,
      apellido: clienteData.apellido,
      correo: clienteData.correo,
      telefono: clienteData.telefono,
      estado: clienteData.estado,
      activo: clienteData.activo,
      nueva_contrasena: passwordValidation.nueva
    };

    setUpdatingPassword(true);
    try {
      const response = await fetch(`${CLIENTES_SITIO_ENDPOINT}/${idCliente}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const resultado = await response.json().catch(() => ({}));
      if (!response.ok || resultado?.estado !== 1) {
        throw new Error(resultado?.resultado || 'No se pudo actualizar la contrasena');
      }
      notie.alert({ type: 'success', text: 'Contrasena actualizada correctamente.', time: 2 });
      setPass({ nueva: '', confirmar: '' });
    } catch (error) {
      console.error('Error actualizando contrasena:', error);
      notie.alert({ type: 'error', text: error.message || 'No se pudo actualizar la contrasena', time: 3 });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const isDisabled = loading || !isEditing;

  return (
    <Fragment>
      <div className="container-fluid">
        <div className="row">

          <div className="col-sm-12 col-xl-12 xl-100">
            <div className="card height-equal mt-2">
              <div className="card-body">
                <button
                  className="btn btn-outline-light-2x txt-dark mb-4"
                  onClick={goBack}
                  disabled={saving}
                >
                  <i className="icofont icofont-arrow-left" />Regresar
                </button>

                <h4 className='pb-4'><strong>Informacion del Cliente</strong></h4>
                <div className="row">
                  <div className="form-group col-sm-6">
                    <label className="form-label">Nombre (s)</label>
                    <input className="form-control" name="nombre" value={clienteData.nombre} onChange={handleDataChange} disabled={isDisabled} />
                  </div>
                  <div className="form-group col-sm-6">
                    <label className="form-label">Apellido (s)</label>
                    <input className="form-control" name="apellido" value={clienteData.apellido} onChange={handleDataChange} disabled={isDisabled} />
                  </div>
                </div>
                <div className="row">
                  <div className="form-group col-sm-6">
                    <label className="form-label">Correo</label>
                    <input className="form-control" name="correo" type="email" value={clienteData.correo} onChange={handleDataChange} disabled={isDisabled} />
                  </div>
                  <div className="form-group col-sm-6">
                    <label className="form-label">Telefono</label>
                    <input className="form-control" name="telefono" value={clienteData.telefono} onChange={handleDataChange} disabled={isDisabled} />
                  </div>
                </div>
                <div className="row">
                  <div className="form-group col-sm-6">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-control"
                      name="estado"
                      value={clienteData.estado}
                      onChange={handleDataChange}
                      disabled={isDisabled}
                    >
                      <option value="Activo">Activo</option>
                      <option value="Inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-sm-12 text-right">
                    <Button className="btn btn-success" onClick={guardarCliente} loading={saving} disabled={isDisabled}>
                      Actualizar Cliente
                    </Button>
                  </div>
                </div>

                <hr className="mt-4 mb-4" />

                <h5 className="mb-3">Reenvio de contraseña (Acceso del Cliente)</h5>
                <small className="text-muted d-block mt-2">Envía un correo automático al usuario con su contraseña actual.</small>

                <div className="d-flex justify-content-end mt-3">
                  <button className="btn btn-primary" type="button" onClick={reenviarContrasena} disabled={isDisabled || resending}>
                    {resending ? 'Enviando...' : 'Reenviar contraseña'}
                  </button>
                </div>

                <hr className="mt-4 mb-4" />

                <h5 className="mb-3">Cambiar Contraseña (Acceso del Cliente)</h5>

                <div className="row">

                  <div className="form-group col-sm-6">
                    <label className="form-label">Nueva Contraseña</label>
                    <input
                      className="form-control"
                      type="password"
                      value={pass.nueva}
                      onChange={e => handlePassChange('nueva', e.target.value)}
                      disabled={isDisabled}
                    />
                    <small className="form-text text-success" style={{ color: '#4CAF50' }}>minimo 5 caracteres</small>
                  </div>

                  <div className="form-group col-sm-6">
                    <label className="form-label">Confirmar Contraseña</label>
                    <input
                      className="form-control"
                      type="password"
                      value={pass.confirmar}
                      onChange={e => handlePassChange('confirmar', e.target.value)}
                      disabled={isDisabled}
                    />
                    <small className="form-text text-success" style={{ color: '#4CAF50' }}>minimo 5 caracteres</small>
                  </div>
                </div>
                <small className="text-muted d-block mt-2">Deja ambos campos vacios si no deseas cambiar la contraseña.</small>

                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={guardarNuevaContrasena}
                    disabled={isDisabled || updatingPassword}
                  >
                    {updatingPassword ? 'Guardando...' : 'Guardar contrase\u00f1a nueva'}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default ActualizarCliente;
